/**
 * Network Status Manager
 *
 * Monitors network connectivity and provides callbacks for online/offline events.
 *
 * Requirements: 5.4
 */

// Use a safe logger that doesn't throw if not initialized
const safeLogger = {
    info: (message: string, ...args: unknown[]) => {
        try {
            const { logger } = require('../logging')
            logger.info(message, ...args)
        } catch {
            // Logger not initialized - silently ignore in tests
        }
    },
    warn: (message: string, ...args: unknown[]) => {
        try {
            const { logger } = require('../logging')
            logger.warn(message, ...args)
        } catch {
            // Logger not initialized - silently ignore in tests
        }
    },
    error: (message: string, ...args: unknown[]) => {
        try {
            const { logger } = require('../logging')
            logger.error(message, ...args)
        } catch {
            // Logger not initialized - silently ignore in tests
        }
    },
}

/**
 * Network status listener callback
 */
export type NetworkStatusListener = (isOnline: boolean) => void

/**
 * Network Status Manager
 *
 * Singleton class that monitors network connectivity using the browser's
 * navigator.onLine API and online/offline events.
 */
export class NetworkStatusManager {
    private static instance: NetworkStatusManager | null = null
    private listeners: Set<NetworkStatusListener> = new Set()
    private _isOnline: boolean = navigator.onLine

    private constructor() {
        this.setupEventListeners()
    }

    /**
     * Get or create singleton instance
     */
    static getInstance(): NetworkStatusManager {
        if (!NetworkStatusManager.instance) {
            NetworkStatusManager.instance = new NetworkStatusManager()
        }
        return NetworkStatusManager.instance
    }

    /**
     * Reset singleton instance (useful for testing)
     */
    static resetInstance(): void {
        if (NetworkStatusManager.instance) {
            NetworkStatusManager.instance.cleanup()
            NetworkStatusManager.instance = null
        }
    }

    /**
     * Get current online status
     */
    get isOnline(): boolean {
        return this._isOnline
    }

    /**
     * Setup event listeners for online/offline events
     */
    private setupEventListeners(): void {
        window.addEventListener('online', this.handleOnline)
        window.addEventListener('offline', this.handleOffline)
    }

    /**
     * Handle online event
     */
    private handleOnline = (): void => {
        safeLogger.info('Network connection restored')
        this._isOnline = true
        this.notifyListeners(true)
    }

    /**
     * Handle offline event
     */
    private handleOffline = (): void => {
        safeLogger.warn('Network connection lost')
        this._isOnline = false
        this.notifyListeners(false)
    }

    /**
     * Notify all listeners of status change
     */
    private notifyListeners(isOnline: boolean): void {
        this.listeners.forEach((listener) => {
            try {
                listener(isOnline)
            } catch (error) {
                safeLogger.error('Error in network status listener', { error })
            }
        })
    }

    /**
     * Add a listener for network status changes
     */
    addListener(listener: NetworkStatusListener): () => void {
        this.listeners.add(listener)

        // Return unsubscribe function
        return () => {
            this.listeners.delete(listener)
        }
    }

    /**
     * Remove a listener
     */
    removeListener(listener: NetworkStatusListener): void {
        this.listeners.delete(listener)
    }

    /**
     * Remove all listeners
     */
    removeAllListeners(): void {
        this.listeners.clear()
    }

    /**
     * Cleanup event listeners
     */
    private cleanup(): void {
        window.removeEventListener('online', this.handleOnline)
        window.removeEventListener('offline', this.handleOffline)
        this.removeAllListeners()
    }
}

/**
 * Get the network status manager instance
 */
export function getNetworkStatusManager(): NetworkStatusManager {
    return NetworkStatusManager.getInstance()
}

/**
 * Check if currently online
 */
export function isOnline(): boolean {
    return getNetworkStatusManager().isOnline
}

/**
 * Add a listener for network status changes
 */
export function addNetworkStatusListener(
    listener: NetworkStatusListener
): () => void {
    return getNetworkStatusManager().addListener(listener)
}

/**
 * Wait for the browser to come back online.
 * Resolves immediately if already online, otherwise listens for the
 * `online` event on `window` and resolves when it fires.
 * The event listener is cleaned up after resolution.
 */
export function waitForOnline(): Promise<void> {
    if (navigator.onLine) {
        return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
        const handler = () => {
            window.removeEventListener('online', handler)
            resolve()
        }
        window.addEventListener('online', handler)
    })
}
